import React, { useState, useMemo } from 'react';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Skeleton,
  Box,
  Chip,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { format } from 'date-fns';
import { parseHistoryDate } from './subscriptionData';

const TYPE_CONFIG = {
  RC: {
    label: 'Credit Purchased',
    icon: AddCircleOutlineIcon,
    color: '#059669',
    bg: 'rgba(5, 150, 105, 0.08)',
    amountPrefix: '+',
    amountColor: '#059669',
  },
  CU: {
    label: 'Credit Used',
    icon: RemoveCircleOutlineIcon,
    color: '#dc2626',
    bg: 'rgba(220, 38, 38, 0.08)',
    amountPrefix: '−',
    amountColor: '#dc2626',
  },
};

function TypeCell({ eventType, eventTypeDisplay }) {
  const config = TYPE_CONFIG[eventType] || { label: eventTypeDisplay ?? '—', icon: null, color: '#64748b', bg: 'rgba(100, 116, 139, 0.08)' };
  const Icon = config.icon;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {Icon && <Icon sx={{ fontSize: 20, color: config.color }} />}
      <Chip
        label={config.label}
        size="small"
        sx={{
          height: 24,
          fontWeight: 600,
          fontSize: '0.75rem',
          bgcolor: config.bg,
          color: config.color,
          border: `1px solid ${config.color}20`,
          '& .MuiChip-label': { px: 1 },
        }}
      />
    </Box>
  );
}

const DEFAULT_ROWS_PER_PAGE = 10;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

export default function VoiceCreditUsageTable({ rows, loading, parseDate }) {
  const parse = parseDate || parseHistoryDate;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const paginatedRows = useMemo(() => {
    if (!rows?.length) return [];
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, page, rowsPerPage]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: 'text.primary' }}>
        Voice-Credit Usage
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Date-time, type (used or added), amount, balance — selected date range
      </Typography>
      {loading ? (
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
      ) : (
        <>
          <TableContainer sx={{ maxHeight: 420, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Table stickyHeader size="small" sx={{ minWidth: 520 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', color: 'text.secondary', fontSize: '0.8125rem', py: 1.5 }}>
                    Date & time
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', color: 'text.secondary', fontSize: '0.8125rem', py: 1.5 }}>
                    Type
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.50', color: 'text.secondary', fontSize: '0.8125rem', py: 1.5 }}>
                    Amount
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.50', color: 'text.secondary', fontSize: '0.8125rem', py: 1.5 }}>
                    Balance
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!paginatedRows.length ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No transactions in this period.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRows.map((row) => {
                  const d = parse(row.date_time || row.created_at);
                  const dateStr = d ? format(d, 'MMM d, yyyy HH:mm') : row.date_time || row.created_at || '—';
                  const amount = row.credit_used ?? row.credits_added ?? row.voice_credits;
                  const config = TYPE_CONFIG[row.event_type] || {};
                  const prefix = config.amountPrefix ?? '';
                  const amountDisplay = amount != null ? (prefix ? `${prefix}${amount}` : String(amount)) : '—';
                  return (
                    <TableRow
                      key={row.id}
                      sx={{
                        '&:hover': { bgcolor: 'action.hover' },
                        '&:not(:last-child) td': { borderBottom: '1px solid', borderColor: 'divider' },
                      }}
                    >
                      <TableCell sx={{ py: 1.5, fontSize: '0.875rem', color: 'text.primary' }}>
                        {dateStr}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <TypeCell eventType={row.event_type} eventTypeDisplay={row.event_type_display} />
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          py: 1.5,
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          color: row.event_type === 'RC' ? TYPE_CONFIG.RC?.amountColor : row.event_type === 'CU' ? TYPE_CONFIG.CU?.amountColor : 'text.primary',
                        }}
                      >
                        {amountDisplay}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.5 }}>
                        <Box
                          component="span"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.9375rem',
                            color: 'text.primary',
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: 'grey.100',
                            display: 'inline-block',
                            minWidth: 56,
                            textAlign: 'right',
                          }}
                        >
                          {row.balance ?? '—'}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {rows?.length > 0 && (
            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              sx={{ borderTop: '1px solid', borderColor: 'divider' }}
            />
          )}
        </>
      )}
    </Paper>
  );
}
